/**
 * B000221 ロケーション照会
 */
import { Column, Query } from "@material-table/core";
import { B000221Find, B000221FindRequest, catchRestError } from "clients/rest";
import {
  msgState,
  SFrame,
  SLabel,
  SNormalButton,
  STable,
  STextField,
} from "components";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  locationCd: string;
  shohinKana: string;
}>;

// テーブル定義
type Data = Partial<{
  gyoNo: number;
  sokoNmr: string;
  locationCd: string;
  shohinNmr: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  locationCd: "",
  shohinKana: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "2rem", align: "right" },
  { field: "sokoNmr", title: "倉庫名", width: "10rem" },
  { field: "locationCd", title: "ロケーションコード", width: "10rem" },
  { field: "shohinNmr", title: "商品名", width: "10rem" },
];

// フォーカスIDの設定
const focusId = "B000221";

export const B000221 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

//B000221ロケーション照会
const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログの開閉を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  //初期表示
  useEffect(() => {
    focus(1);
  });

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B000221FindRequest = {
        locationCd: fields.locationCd,
        shohinKana: fields.shohinKana,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        sort: ["locationCd,asc"],
      };
      // API呼出し
      B000221Find(param)
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({ data: [], page: 0, totalCount: 0 });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  //レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="ロケーション照会"
        gridTemplateColumns="6rem 8rem 14rem 1rem 2rem "
      >
        <SLabel>ロケーション</SLabel>
        <STextField
          label="ロケーションコード"
          name="locationCd"
          {...register(1)}
          size={14}
          location
        />
        <SLabel gridColumn="1">商品名カナ</SLabel>
        <STextField
          label="商品名カナ"
          name="shohinKana"
          {...register(2)}
          gridColumn="span 2"
          size={40}
        />

        <SNormalButton
          label={labels.buttons.search}
          gridRow={2}
          gridColumn={5}
          onClick={handleClickSearchButton}
          {...register(3)}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
